import { useState } from "react";
import { Lock, ArrowRight, Check } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const INTERESTS = [
  "1:1 Coaching Session",
  "6-Session Deep Dive",
  "The Rewired Life Live Cohort",
  "Workplace Workshop",
];

export default function Waitlist() {
  useSeo({
    title: "Waitlist — Pip Bennett",
    description: "1:1 coaching with Pip Bennett is currently at capacity. Add your name to the waitlist and you'll be the first to know when a place opens.",
    canonical: "https://pipbennett.com/waitlist",
  });
  const [form, setForm] = useState({ name: "", email: "", interest: INTERESTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Unable to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 md:py-32 bg-cream" data-testid="page-waitlist">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <SectionReveal>
          <div className="inline-flex items-center gap-2 bg-gold/15 text-gold-dark text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 rounded-full mb-6">
            <Lock size={12} strokeWidth={2} /> Books Closed
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.05]">
            Join the <em className="italic text-sage">waitlist</em>.
          </h1>
          <p className="mt-6 text-ink-soft max-w-2xl leading-relaxed text-lg">
            Pip's 1:1 practice is intentionally small and currently at capacity. Add your name here — when a place opens,
            she'll email the waitlist first (usually 3–5 places, once a quarter).
          </p>
        </SectionReveal>

        {done ? (
          <SectionReveal>
            <div className="mt-14 bg-white border border-hairline rounded-2xl p-10 md:p-14 text-center" data-testid="waitlist-success">
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-sage text-cream inline-flex items-center justify-center">
                <Check size={24} strokeWidth={1.3} />
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-ink">You're on the list.</h2>
              <p className="mt-5 text-ink-soft max-w-lg mx-auto leading-relaxed">
                Pip personally reviews the waitlist before each opening. You'll hear directly from her when a place
                matches your timing.
              </p>
            </div>
          </SectionReveal>
        ) : (
          <SectionReveal delay={100}>
            <form onSubmit={submit} className="mt-14 bg-white border border-hairline rounded-2xl p-8 md:p-12 space-y-6" data-testid="waitlist-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">First Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-cream-soft border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="waitlist-name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-cream-soft border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="waitlist-email"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-2 text-ink-soft">What are you interested in?</label>
                <select
                  value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  className="w-full bg-cream-soft border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                  data-testid="waitlist-interest"
                >
                  {INTERESTS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-2 text-ink-soft">A note to Pip (optional)</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What's bringing you here? What would you like to shift?"
                  className="w-full bg-cream-soft border border-hairline rounded-2xl px-6 py-4 text-ink focus:outline-none focus:border-sage transition resize-none"
                  data-testid="waitlist-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all disabled:opacity-50"
                data-testid="waitlist-submit"
              >
                {loading ? "Adding you…" : "Join the Waitlist"}
                {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
              </button>
            </form>
          </SectionReveal>
        )}
      </div>
    </section>
  );
}
